/**
 * ApplicantIntakeModal — entry point for "+ New Interview" on /admissions.
 *
 * Creates a new school_students row with status='candidate' and
 * immediately kicks off the adaptive interview. The interviewer never
 * has to flip back to the pool — they land on
 * /admissions/interview/{id} with round 1's first question already
 * generated.
 *
 * Required fields are intentionally minimal (name + grade). Everything
 * else (contact, parents, address) can be captured during the
 * interview or post-acceptance — the funnel optimizes for "get the
 * candidate sitting at the table" speed.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCreateStudent,
  useStartInterview,
} from "../../api/school";

interface Props {
  schoolId: string;
  onClose: () => void;
}

export function ApplicantIntakeModal({ schoolId, onClose }: Props) {
  const { t } = useTranslation();
  const createStudent = useCreateStudent();
  const startInterview = useStartInterview();

  const [nameZh, setNameZh] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [grade, setGrade] = useState("G7");
  const [studentCode, setStudentCode] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!nameZh.trim()) {
      setErr(t("admissions.intake.errNameRequired"));
      return;
    }
    if (!grade.trim()) {
      setErr(t("admissions.intake.errGradeRequired"));
      return;
    }
    setErr(null);
    try {
      const student = await createStudent.mutateAsync({
        school_id: schoolId,
        name_zh: nameZh.trim(),
        name_en: nameEn.trim() || null,
        grade: grade.trim(),
        student_code: studentCode.trim() || null,
        // Backend StudentCreate defaults status='active' — override so
        // this applicant lands in the candidate pool, not the enrolled
        // roster.
        // @ts-expect-error: status isn't on the hook's payload shape;
        // backend accepts it via passthrough.
        status: "candidate",
      });
      const state = await startInterview.mutateAsync({
        student_id: student.id,
      });
      // Hard nav — the interview UX is a different page rhythm than
      // the pool browser. Using window.location keeps the rest of the
      // route tree clean (no nested router state to coordinate).
      window.location.href = `/admissions/interview/${state.admission_id}`;
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const busy = createStudent.isPending || startInterview.isPending;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-800">{t("admissions.intake.title")}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("admissions.intake.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
          >
            ×
          </button>
        </header>

        <div className="p-6 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs text-slate-600 mb-1">
                {t("admissions.intake.nameZh")} <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                value={nameZh}
                onChange={(e) => setNameZh(e.target.value)}
                placeholder={t("admissions.intake.nameZhPlaceholder")}
                className="w-full px-2 py-1.5 rounded border border-slate-200"
                autoFocus
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-600 mb-1">{t("admissions.intake.nameEn")}</span>
              <input
                type="text"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder={t("admissions.intake.nameEnPlaceholder")}
                className="w-full px-2 py-1.5 rounded border border-slate-200"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs text-slate-600 mb-1">
                {t("admissions.intake.grade")} <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder={t("admissions.intake.gradePlaceholder")}
                className="w-full px-2 py-1.5 rounded border border-slate-200"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs text-slate-600 mb-1">{t("admissions.intake.studentCode")}</span>
              <input
                type="text"
                value={studentCode}
                onChange={(e) => setStudentCode(e.target.value)}
                placeholder={t("admissions.intake.studentCodePlaceholder")}
                className="w-full px-2 py-1.5 rounded border border-slate-200"
              />
            </label>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            {t("admissions.intake.description")}
          </p>

          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>

        <footer className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !nameZh.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {busy ? t("admissions.intake.submitting") : t("admissions.intake.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
